/**
 * CaptureHowTo — the collapsible "how to grab a bug's details" disclosure on the
 * public landing page (story epic-uat-portal-001-9).
 *
 * Built on a native <details>/<summary> so it is keyboard- and
 * screen-reader-accessible with no JS. It teaches, in plain language, how to
 * capture the four things that make a report reproducible BEFORE navigating
 * away: the URL, a screenshot, the DevTools Console, and the DevTools Network
 * tab (design goal 2, "lower the floor on good bug reports").
 *
 * Public: no account needed.
 */

export function CaptureHowTo() {
  return (
    <section
      aria-labelledby="capture-heading"
      data-testid="capture-howto"
      className="mx-auto w-full max-w-4xl px-6 py-12"
    >
      <h2 id="capture-heading" className="mb-2 text-2xl font-bold">
        Before you move on — grab the details
      </h2>
      <p className="mb-6 text-sm text-muted-foreground">
        The best reports include what the page was doing when it broke. Capture
        these BEFORE you navigate away — once you leave the page, the errors are
        gone.
      </p>

      <details
        data-testid="capture-details"
        className="rounded-lg border border-gray-200 p-5"
      >
        <summary
          data-testid="capture-summary"
          className="cursor-pointer select-none font-semibold"
        >
          How to capture a bug&apos;s details
        </summary>

        <div className="mt-5 flex flex-col gap-6 text-sm">
          <div data-testid="capture-url">
            <h3 className="mb-1 font-semibold">1. The URL</h3>
            <p className="text-muted-foreground">
              Copy the full web address from your browser&apos;s address bar at
              the top of the window. It tells us exactly which page you were on.
            </p>
          </div>

          <div data-testid="capture-screenshot">
            <h3 className="mb-1 font-semibold">2. A screenshot</h3>
            <p className="text-muted-foreground">
              A picture of what you saw makes almost every problem faster to fix.
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                <span className="font-medium">Mac:</span> press{' '}
                <kbd className="rounded border px-1">Shift</kbd> +{' '}
                <kbd className="rounded border px-1">Cmd</kbd> +{' '}
                <kbd className="rounded border px-1">4</kbd>, then drag a box
                around the area.
              </li>
              <li>
                <span className="font-medium">Windows:</span> press{' '}
                <kbd className="rounded border px-1">Win</kbd> +{' '}
                <kbd className="rounded border px-1">Shift</kbd> +{' '}
                <kbd className="rounded border px-1">S</kbd>, then drag a box
                around the area.
              </li>
            </ul>
          </div>

          <div data-testid="capture-console">
            <h3 className="mb-1 font-semibold">
              3. The DevTools Console (red errors)
            </h3>
            <p className="text-muted-foreground">
              Open your browser&apos;s developer tools — press{' '}
              <kbd className="rounded border px-1">F12</kbd> on Windows, or{' '}
              <kbd className="rounded border px-1">Opt</kbd> +{' '}
              <kbd className="rounded border px-1">Cmd</kbd> +{' '}
              <kbd className="rounded border px-1">I</kbd> on Mac — and click the{' '}
              <span className="font-medium">Console</span> tab. Any lines in red
              are errors. Screenshot them.
            </p>
          </div>

          <div data-testid="capture-network">
            <h3 className="mb-1 font-semibold">
              4. The DevTools Network tab (failed requests)
            </h3>
            <p className="text-muted-foreground">
              In the same developer tools, click the{' '}
              <span className="font-medium">Network</span> tab, then reload the
              page. Look for rows that are red or show a status of 4xx or 5xx
              (like 404 or 500) — those are requests that failed. Screenshot
              them.
            </p>
          </div>
        </div>
      </details>
    </section>
  );
}
